import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Order } from "../../models/order.model";
import { FormControl } from "@angular/forms";
import { ProductOrder } from "../../models/product-order.model";
import { AuthService } from "../../services/auth.service";
import { RolesEnum } from "../../enums/roles.enum";
import { ExternalProductionService } from "../../services/external-production.service";
import { OrderService } from "../../services/order.service";
import { FileType } from '../../enums/zip-file-type.enum';
import { OrderState } from "../../enums/order-state.enum";
import { OrganizationService } from "../../services/organization.service";

@Component({
    selector: 'app-download-order-files',
    templateUrl: './download-order-files.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DownloadOrderFilesComponent implements OnInit {
  protected readonly RolesEnum = RolesEnum;
  protected readonly OrderState = OrderState;

  private dialogRef = inject(MatDialogRef<DownloadOrderFilesComponent>)
  data = inject<{
    order: Order, product: ProductOrder,
    isSelectedSubcontractor: boolean
  }>(MAT_DIALOG_DATA)
  authService = inject(AuthService);
  orderService = inject(OrderService);
  externalProductionService = inject(ExternalProductionService);
  organizationService = inject(OrganizationService);

  originalName = new FormControl(false, { nonNullable: true });
  customerFiles = new FormControl(false, { nonNullable: true });
  proofFiles = new FormControl(false, { nonNullable: true });
  prepressFiles = new FormControl(false, { nonNullable: true });
  productionFiles = new FormControl(false, { nonNullable: true });
  progress = false;
  error: unknown;
  canDownloadProd = false;
  canDownloadFilesAndProofs = false;

  get noneSelected(): boolean {
    return !this.customerFiles.value &&
      !this.proofFiles.value &&
      !this.prepressFiles.value &&
      !this.productionFiles.value;
  }

  ngOnInit(): void {
    this.error = null;
    // The connected user is an Okto admin console user
    if (this.authService.hasRole([RolesEnum.ORDER_WRITE, RolesEnum.PRODUCTION_WRITE, RolesEnum.WORKSHOP_WRITE])) {
      this.canDownloadProd = true;
      this.canDownloadFilesAndProofs = true;
      this.prepressFiles.setValue(true);
    } else {
      if (this.data.product) { // The user click on a specific product
        this.canDownloadFilesAndProofs = this.authService.currentUser.customerIds.includes(this.data.order.customerId);
        this.canDownloadProd = this.data.isSelectedSubcontractor;
      } else { // The user click on general file downloading button
        this.canDownloadFilesAndProofs = true;
        this.originalName.setValue(!this.organizationService.currentOrganization?.id);
      }
    }
    this.productionFiles.setValue(this.canDownloadProd);
    this.customerFiles.setValue(this.canDownloadFilesAndProofs);
    this.proofFiles.setValue(this.canDownloadFilesAndProofs);
  }

  /**
   * Close dialog without any actions
   */
  cancel(): void {
    this.dialogRef.close(null);
  }

  download(): Promise<void> {
    this.progress = true;
    this.error = null;
    const types: FileType[] = [];
    if (this.customerFiles.value) {
      types.push(FileType.Customer);
    }
    if (this.proofFiles.value) {
      types.push(FileType.Proof);
    }
    if (this.prepressFiles.value) {
      types.push(FileType.Pao);
    }
    if (this.productionFiles.value) {
      types.push(FileType.Prod);
    }
    return this.orderService.getOrderZip(this.data.order.id, this.data.product?.poId, types, this.originalName.value)
      .then(url => {
        if (!url) {
          this.error = new Error('noFiles');
          return;
        }
        const a = document.createElement('a');
        a.href = url;
        a.click();
      })
      .catch(err => {
        console.error(err);
        this.error = err;
      })
      .finally(() => {
        this.progress = false;
      });
  }

  /**
   * Check if the current organization is the external production organization
   * @returns True if organization are equals
   */
  isOwner(): boolean {
    if (!this.organizationService.currentOrganization?.id) {
      return false;
    }
    return this.data.order.organizationId === this.organizationService.currentOrganization.id;
  }
}
